import React, { useEffect, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader,
  DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { NavLink } from "react-router-dom";
import {
  Users, ShieldCheck, Signature, FileText, Lock, Upload, Download, Trash2,
  RefreshCw, Loader2, Package, History, Undo2, Link2,
} from "lucide-react";
import { ProductionLinksPicker } from "@/components/ProductionLinksPicker";
import { formatApiError as _fmt } from "@/contexts/AuthContext";
// keep local import name intact for below usage


/**
 * Production Rate Cards — INTERNAL ONLY.
 *
 * Separate page from Admin Rates on purpose: the workbooks here are
 * pre-formatted for the internal admin platform and must NEVER surface to
 * agents, dealers, or the public rate library UI. Only super admin and
 * users with `onboarding.production.manage` can reach this route.
 *
 * Data is stored on the same `rate_library` collection with a
 * `production_only: true` flag — the dedicated `/production/rate-cards`
 * endpoint enforces the role gate server-side (403 for agents) and the
 * regular Admin Rates page filters these out of its list.
 */
function AdminSidebar() {
  const linkCls = ({ isActive }) =>
    `px-3 py-2 rounded-sm text-sm font-medium flex items-center gap-2 transition-colors ${
      isActive
        ? "bg-[#0B132B] text-white"
        : "text-[#0B132B] hover:bg-[#0B132B]/5"
    }`;
  return (
    <aside className="lg:sticky lg:top-20 lg:h-fit">
      <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37] mb-3">
        Admin
      </div>
      <nav className="flex lg:flex-col gap-1">
        <NavLink to="/admin/users" className={linkCls} data-testid="admin-nav-users">
          <Users size={15} /> Users
        </NavLink>
        <NavLink to="/admin/agencies" className={linkCls} data-testid="admin-nav-agencies">
          <ShieldCheck size={15} /> Agencies
        </NavLink>
        <NavLink to="/admin/agents" className={linkCls} data-testid="admin-nav-agents">
          <Signature size={15} /> Agents
        </NavLink>
        <NavLink to="/admin/proposals" className={linkCls} data-testid="admin-nav-proposals">
          <FileText size={15} /> All proposals
        </NavLink>
        <NavLink to="/admin/rates" className={linkCls} data-testid="admin-nav-rates">
          <FileText size={15} /> Rate sheet
        </NavLink>
        <NavLink to="/admin/production-rate-cards" className={linkCls} data-testid="admin-nav-production-rate-cards">
          <Lock size={15} /> Production rate cards
        </NavLink>
      </nav>
    </aside>
  );
}

function UploadDialog({ onUploaded }) {
  const [programName, setProgramName] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [links, setLinks] = useState({ vertical_key: null, product_ids: [] });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!programName.trim()) { toast.error("Program name is required"); return; }
    if (!file) { toast.error("Pick a file"); return; }
    if (!links.product_ids.length) { toast.error("Pick at least one program so onboarding knows where this workbook belongs"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Everything on this page is production-only by design. Vertical +
      // category are internal audit tags — pre-set to "internal" so the
      // production team never has to think about them.
      fd.append("vertical", "automotive");
      fd.append("category", "Production");
      fd.append("program_name", programName.trim());
      if (notes.trim()) fd.append("notes", notes.trim());
      fd.append("production_only", "true");
      if (links.vertical_key) fd.append("onboarding_vertical_key", links.vertical_key);
      fd.append("onboarding_product_ids", links.product_ids.join(","));
      await api.post("/admin/rate-library/sheets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded "${programName.trim()}"`);
      onUploaded();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="prod-rate-upload-dialog">
      <DialogHeader>
        <DialogTitle>Upload production rate card</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div className="bg-[#FFFBEB] border border-[#F2C94C] rounded-sm p-2.5 text-[11px] text-[#0B132B] leading-snug">
          <strong>Internal only.</strong> This workbook must be the version
          formatted for the admin install platform — not the customer-facing
          rate card. It stays hidden from agents and the public rate library.
        </div>
        <div>
          <Label className="text-xs">Program name *</Label>
          <Input value={programName} onChange={(e) => setProgramName(e.target.value)}
            placeholder="e.g. GoForever Battery — Install Workbook"
            className="h-9 rounded-sm" data-testid="prod-rate-upload-name" />
        </div>
        <ProductionLinksPicker value={links} onChange={setLinks} testId="prod-rate-upload-links" />
        <div>
          <Label className="text-xs">Notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            placeholder="e.g. Effective Q1 2026 — replaces v3 workbook"
            className="rounded-sm text-sm" data-testid="prod-rate-upload-notes" />
        </div>
        <div>
          <Label className="text-xs">File * (.xlsx / .xls / .csv / .pdf)</Label>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.pdf,.csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-xs mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-[#0B132B] file:text-white file:text-xs file:font-bold hover:file:bg-[#0B132B]/90"
            data-testid="prod-rate-upload-file" />
        </div>
      </div>
      <DialogFooter>
        <Button size="sm" className="bg-[#0B132B] text-white" onClick={submit} disabled={saving}
          data-testid="prod-rate-upload-submit">
          {saving ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Uploading…</> : <><Upload size={12} className="mr-1.5" /> Upload</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function CardRow({ card, onDownload, onReplace, onDelete, onHistory, catalog, onLinksSaved }) {
  const replaceRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [links, setLinks] = useState({ vertical_key: card.onboarding_vertical_key || null, product_ids: card.onboarding_product_ids || [] });
  const [saving, setSaving] = useState(false);
  const linkedNames = (card.onboarding_product_ids || []).map((id) => catalog?.products.find((p) => p.id === id)?.name).filter(Boolean);
  const verticalName = catalog?.verticals.find((v) => v.key === card.onboarding_vertical_key)?.name;
  const saveLinks = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/rate-library/sheets/${card.id}/production-links`, { onboarding_vertical_key: links.vertical_key, onboarding_product_ids: links.product_ids });
      toast.success("Program links saved");
      setEditing(false);
      onLinksSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <div className="p-2.5 border border-[#E9ECEF] rounded-sm bg-white" data-testid={`prod-rate-row-${card.id}`}>
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-bold text-[#0B132B] truncate">{card.program_name}</div>
          <span className="text-[9px] uppercase tracking-wider font-bold text-white bg-[#0B132B] border border-[#0B132B] px-1.5 py-0.5 rounded-sm shrink-0">
            Internal
          </span>
          {verticalName && <span className="text-[9px] uppercase tracking-wider font-bold text-[#0B132B] bg-[#D4AF37]/20 border border-[#D4AF37] px-1.5 py-0.5 rounded-sm shrink-0" data-testid={`prod-rate-vertical-${card.id}`}>{verticalName}</span>}
        </div>
        <div className="text-[10px] text-[#6C757D] truncate">
          {card.filename} · uploaded {new Date(card.uploaded_at).toLocaleDateString()}
          {card.notes ? ` · ${card.notes}` : ""}
        </div>
        <button type="button" onClick={() => setEditing((e) => !e)} className="mt-1 text-[11px] text-left hover:underline" data-testid={`prod-rate-links-toggle-${card.id}`}>
          {linkedNames.length ? (
            <span className="text-[#0B132B]"><Link2 size={10} className="inline mr-1 text-[#D4AF37]" />Installs: <b>{linkedNames.join(", ")}</b></span>
          ) : (
            <span className="text-amber-700 font-semibold"><Link2 size={10} className="inline mr-1" />Not linked to any program — click to link so onboarding can find it</span>
          )}
        </button>
      </div>
      <Button size="icon" variant="outline" onClick={onDownload}
        className="h-7 w-7 rounded-sm" title="Download workbook"
        data-testid={`prod-rate-download-${card.id}`}>
        <Download size={11} />
      </Button>
      <input ref={replaceRef} type="file" accept=".xlsx,.xls,.pdf,.csv" className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onReplace(f);
          if (replaceRef.current) replaceRef.current.value = "";
        }}
        data-testid={`prod-rate-replace-input-${card.id}`} />
      <Button size="icon" variant="outline" onClick={() => replaceRef.current?.click()}
        className="h-7 w-7 rounded-sm border-[#D4AF37] text-[#0B132B] hover:bg-[#D4AF37]/10"
        title="Replace with updated version"
        data-testid={`prod-rate-replace-${card.id}`}>
        <RefreshCw size={11} />
      </Button>
      <Button size="icon" variant="outline" onClick={onHistory}
        disabled={!(card.history && card.history.length > 0)}
        className="h-7 w-7 rounded-sm disabled:opacity-40"
        title={card.history && card.history.length ? `${card.history.length} previous version${card.history.length === 1 ? "" : "s"} available` : "No previous versions saved yet"}
        data-testid={`prod-rate-history-${card.id}`}>
        <History size={11} />
      </Button>
      <Button size="icon" variant="outline" onClick={onDelete}
        className="h-7 w-7 rounded-sm border-red-300 text-red-700 hover:bg-red-50" title="Delete"
        data-testid={`prod-rate-delete-${card.id}`}>
        <Trash2 size={11} />
      </Button>
    </div>
    {editing && (
      <div className="mt-2 pt-2 border-t border-dashed border-[#E9ECEF]" data-testid={`prod-rate-links-editor-${card.id}`}>
        <ProductionLinksPicker value={links} onChange={setLinks} testId={`prod-rate-links-${card.id}`} compact />
        <div className="flex gap-2 mt-2">
          <Button size="sm" className="h-8 rounded-sm bg-[#0B132B] text-white" onClick={saveLinks} disabled={saving} data-testid={`prod-rate-links-save-${card.id}`}>
            {saving ? <Loader2 className="animate-spin" size={12} /> : "Save links"}
          </Button>
          <Button size="sm" variant="ghost" className="h-8 rounded-sm" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      </div>
    )}
    </div>
  );
}

export default function AdminProductionRateCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [catalog, setCatalog] = useState(null);
  useEffect(() => { api.get("/onboarding/catalog").then(({ data }) => setCatalog(data)).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/production/rate-cards");
      setCards(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const download = async (c) => {
    try {
      const { data, headers } = await api.get(`/admin/rate-library/sheets/${c.id}/download`, {
        responseType: "blob",
        params: { disposition: "attachment" },
      });
      const cd = headers["content-disposition"] || "";
      const match = /filename\*?=(?:UTF-8'')?"?([^;"]+)"?/i.exec(cd);
      const fn = (match && decodeURIComponent(match[1])) || c.filename || "workbook.xlsx";
      const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = fn; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const replace = async (c, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/rate-library/sheets/${c.id}/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Replaced "${c.program_name}"`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete production rate card "${c.program_name}"?`)) return;
    try {
      await api.delete(`/admin/rate-library/sheets/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <AdminSidebar />
        <div>
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37] mb-1">Internal • Production team</div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0B132B]">Production rate cards</h1>
            <p className="text-sm text-[#6C757D] mt-1 max-w-2xl">
              Master install workbooks — pre-formatted for the admin platform — surfaced when production takes ownership of a
              new dealer. <strong>Hidden from agents and the public rate library.</strong> Replace any card in place when a program's rates update.
            </p>
          </div>

          <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6" data-testid="prod-rate-list-card">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Package size={16} className="text-[#0B132B]" />
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Master install workbooks</div>
                <span className="text-[10px] text-[#6C757D]">({cards.length})</span>
              </div>
              <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-sm bg-[#0B132B] text-white hover:bg-[#0B132B]/90 h-9"
                    data-testid="prod-rate-upload-btn">
                    <Upload size={12} className="mr-1.5" /> Upload workbook
                  </Button>
                </DialogTrigger>
                <UploadDialog onUploaded={() => { setUploadOpen(false); load(); }} />
              </Dialog>
            </div>
            {loading ? (
              <div className="text-sm text-[#6C757D] py-6 text-center">
                <Loader2 className="animate-spin inline mr-2" size={13} /> Loading…
              </div>
            ) : cards.length === 0 ? (
              <div className="text-sm text-[#6C757D] italic py-8 text-center border border-dashed border-[#E9ECEF] rounded-sm">
                No production rate cards yet. Upload the master workbook for each program the production team installs.
              </div>
            ) : (
              <div className="space-y-2" data-testid="prod-rate-list">
                {cards.map((c) => (
                  <CardRow key={c.id} card={c}
                    onDownload={() => download(c)}
                    onReplace={(file) => replace(c, file)}
                    onDelete={() => remove(c)}
                    onHistory={() => setHistoryFor(c)}
                    catalog={catalog}
                    onLinksSaved={load}
                  />
                ))}
              </div>
            )}
          </Card>
        </div>
      </main>
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        {historyFor && (
          <VersionHistoryDialog
            card={historyFor}
            onRestored={() => { setHistoryFor(null); load(); }}
          />
        )}
      </Dialog>
    </div>
  );
}

function VersionHistoryDialog({ card, onRestored }) {
  const [busyIdx, setBusyIdx] = useState(null);
  // Reverse so newest history entry is on top.
  const versions = [...(card.history || [])].reverse();
  const restore = async (originalIdx) => {
    if (!window.confirm("Roll back to this version? The current file will be pushed into history so you can swap back.")) return;
    setBusyIdx(originalIdx);
    try {
      await api.post(`/admin/rate-library/sheets/${card.id}/restore/${originalIdx}`);
      toast.success("Restored");
      onRestored();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusyIdx(null);
    }
  };
  return (
    <DialogContent className="max-w-lg" data-testid="prod-rate-history-dialog">
      <DialogHeader>
        <DialogTitle>Version history — {card.program_name}</DialogTitle>
      </DialogHeader>
      <div className="text-[11px] text-[#6C757D] mb-2 leading-snug">
        The last 3 uploads are kept automatically on every replace. Restoring pushes the current file back into history so you can flip both ways.
      </div>
      {versions.length === 0 ? (
        <div className="text-sm text-[#6C757D] italic py-4 text-center">
          No previous versions saved yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="prod-rate-history-list">
          {versions.map((v, i) => {
            // The API's history array is oldest→newest; we reversed for display.
            const originalIdx = (card.history || []).length - 1 - i;
            const when = v.replaced_at ? new Date(v.replaced_at) : null;
            return (
              <div key={originalIdx} className="border border-[#E9ECEF] rounded-sm p-2.5 flex items-center gap-3" data-testid={`prod-rate-history-row-${originalIdx}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-[#0B132B] truncate">{v.filename || "unnamed file"}</div>
                  <div className="text-[10px] text-[#6C757D] truncate">
                    replaced {when ? when.toLocaleString() : "—"}{v.replaced_by ? ` · by ${v.replaced_by}` : ""}
                    {v.file_size ? ` · ${(v.file_size / 1024).toFixed(1)} KB` : ""}
                  </div>
                </div>
                <Button size="sm" variant="outline"
                  onClick={() => restore(originalIdx)}
                  disabled={busyIdx !== null}
                  className="h-8 rounded-sm text-xs"
                  data-testid={`prod-rate-history-restore-${originalIdx}`}>
                  {busyIdx === originalIdx ? <Loader2 className="animate-spin mr-1.5" size={11} /> : <Undo2 size={11} className="mr-1" />}
                  Restore
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </DialogContent>
  );
}
